import { appRoutes } from "@/app/router";
import { AppRoute } from "@/app/router/utils";
import { Maybe } from "@/domains/common/types";
import { APP_CONFIG } from "@/domains/global/config";
import { goBack } from "@/store/navigation/navigation-stack/goBack";
import { goUp } from "@/store/navigation/navigation-stack/goUp";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { isEqual, isString, last } from "lodash-es";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { useEffect } from "react";
import {
  type NavigateFunction,
  type NavigateOptions,
  type To,
  matchPath,
  useLocation,
  useNavigate,
} from "react-router";
import { StateSnapshot, VirtuosoHandle } from "react-virtuoso";
import { urlParamsModule } from "@/modules/url-params";
import { SearchEngineParams } from "@/modules/url-params/search-engine-params/types";
import { HomeParams } from "@/modules/url-params/home-params/types";
import { MemCommonMentionKind } from "@mem-labs/common-editor";
import { goForward } from "@/store/navigation/navigation-stack/goForward";
import { windowModule } from "@/modules/window";
import { arePathsEqual } from "./navigation-stack/comparison";
import { INotesViewPageStore } from "@/store/pages/NotesViewPageStore/types";
import { NavigationTabManager } from "@/store/navigation/NavigationTabManager";
import { isMac } from "@/domains/platform/isMac";
import { TabOpeningStrategy } from "@/store/navigation/NavigationTabManager";

type NavigationMouseEvent = React.MouseEvent | MouseEvent | React.KeyboardEvent | KeyboardEvent;

function isCmdClick(event?: NavigationMouseEvent): boolean {
  if (!event) return false;
  return isMac() ? event.metaKey : event.ctrlKey;
}

export class AppStoreNavigationStore extends AppSubStore {
  private navigate: NavigateFunction | null = null;

  stack: To[] = [];
  forwardStack: To[] = [];
  virtuoso: VirtuosoHandle | null = null;
  virtuosoStateStack: Maybe<StateSnapshot>[] = [];
  tabs: NavigationTabManager;

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);
    this.tabs = new NavigationTabManager({
      navigate: null,
      onTabChange: ({ tab, path, openInBackground, isNew, suppressSidePanelRouteHandler }) => {
        // delegate to the side panel
        this.store.sidePanel?.handleTabChange({
          tab,
          path,
          openInBackground,
          isNew,
          suppressSidePanelRouteHandler,
        });
      },
    });

    makeObservable<this, "navigate" | "navigateTo">(this, {
      tabs: true,
      goToIntegrationsZapier: true,
      goToIntegrationsEmail: true,
      goToIntegrationsTextMessage: true,
      goToIntegrationsChromeExtension: true,
      goToIntegrationsApi: true,
      goToSessions: true,
      goToSession: true,
      goToAudioRecordings: true,
      goToAudioRecording: true,
      goToAudioRecordingCreate: true,
      navigate: observable,

      stack: observable,
      forwardStack: observable,
      virtuoso: observable,
      virtuosoStateStack: observable,
      restoreVirtuosoStateFrom: computed,

      setVirtuoso: action,
      useSyncNavigation: action,
      pushToStack: action,

      activePath: computed,
      activeSearchQuery: computed,
      matchActivePath: false,
      navigateTo: false,
      canGoBack: computed,
      canGoForward: computed,
      currentItemId: computed,
      isCurrentlyOnCollectionPage: computed,
      isCurrentlyOnNotePage: computed,

      goUpIfActivePathIs: false,
      goUp: false,
      goBack: false,
      goForward: false,
      goToLogOut: false,
      goToLogIn: false,
      goToCollections: false,
      goToChatConversation: false,
      goToIntegrations: false,
      goToIntegrationsImports: false,
      goToImportFrom1dot0: false,
      goToCollection: false,
      goToNote: false,
      goToTemplates: false,
      goToTemplate: false,
      goToTrash: false,
      goToAdmin: false,
      goToSettings: false,
      goToSettingsEmailPreferences: false,
      goToSettingsSubscriptionManagement: false,
      goToSettingsAccountManagement: false,
      goToSettingsDebugAccount: false,
      goToSettingsDebugSandbox: false,
      goToSettingsExports: false,
      goToSettingsAccountMigration: false,
      goToSearch: false,
      goToInvites: false,
      goToExplore: false,
      goToExploreTopicDetail: false,
      goToNotes: false,
      goToSavedSearch: false,
      openExternalHelpAndSupport: false,
      goToMention: false,
      goToImportBatchCollections: false,
      goToImportBatchNotes: false,
      goToMemIt: false,
    });
  }

  setVirtuoso = (virtuoso: VirtuosoHandle | null) => {
    this.virtuoso = virtuoso;
  };

  get restoreVirtuosoStateFrom() {
    return this.virtuosoStateStack[this.virtuosoStateStack.length - 1];
  }

  useSyncNavigation() {
    const navigate = useNavigate();
    this.navigate = navigate;
    this.tabs.setNavigateFunction(navigate);

    const initialLocation = useLocation();

    useEffect(() => {
      this.tabs.handleUrlChange({
        pathname: initialLocation.pathname,
        search: initialLocation.search,
      });

      if (arePathsEqual(this.activePath, initialLocation.pathname)) {
        return;
      }

      this.pushToStack({
        pathname: initialLocation.pathname,
        search: initialLocation.search,
      });
    }, [initialLocation.pathname, initialLocation.search]);
  }

  pushToStack = (path: To) => {
    this.stack.push(path);
    this.virtuosoStateStack.push(undefined);
  };

  get activePath(): To | undefined {
    return last(this.stack);
  }

  get activeSearchQuery(): string {
    if (!this.activePath || !isString(this.activePath.search)) {
      return "";
    }

    return this.activePath.search;
  }

  get isCurrentlyOnNotePage(): boolean {
    const pathname =
      typeof this.activePath === "string" ? this.activePath : this.activePath?.pathname;

    if (!pathname) {
      return false;
    }

    return !!matchPath("/notes/:noteId", pathname);
  }

  get isCurrentlyOnCollectionPage(): boolean {
    const pathname =
      typeof this.activePath === "string" ? this.activePath : this.activePath?.pathname;

    if (!pathname) {
      return false;
    }

    return !!matchPath("/collections/:collectionId", pathname);
  }

  get currentItemId(): string | undefined {
    const pathname =
      typeof this.activePath === "string" ? this.activePath : this.activePath?.pathname;

    return pathname?.split("/")[2];
  }

  matchActivePath = ({ path }: { path: string }) => {
    if (!this.activePath) {
      return false;
    }

    if (isString(this.activePath)) {
      return matchPath(path, this.activePath);
    }

    if (isString(this.activePath.pathname)) {
      return matchPath(path, this.activePath.pathname);
    }

    return false;
  };

  /**
   * @todo - I wonder if we can modify this to just hook into our utility functions below
   * E.g. we have "goToCollection" push onto the "nav stack", etc.
   *
   * TODO - we also need some more tests/comments about what this is, why it is necessary,
   * and how it works.
   */
  private navigateTo = ({
    appRoute,
    to,
    config,
  }: (
    | {
        appRoute: AppRoute;
        to?: undefined;
      }
    | {
        appRoute?: undefined;
        to: To;
      }
  ) & {
    config?: {
      routerOptions?: NavigateOptions;
      /** resetStack will remove all history. This differs from replace which only replaces the top of the nav stack. */
      resetStack?: boolean;
    };
  }) => {
    if (!this.navigate) {
      return;
    }

    const targetPath = appRoute ? appRoute.path : to;
    const replaceRoute = config?.routerOptions?.replace ?? false;
    const resetStack = config?.resetStack ?? false;

    const topOfStack = last(this.stack);

    if (isEqual(topOfStack, targetPath)) {
      return;
    }

    if (replaceRoute) {
      // There's no need to update virtuoso state if we're replacing the current route.
      this.stack.pop();
      this.stack.push(targetPath);
      this.navigate(targetPath, { replace: true, ...config });
      return;
    }

    if (resetStack) {
      runInAction(() => {
        this.stack = [];
        this.virtuosoStateStack = [];
      });
    }

    // When navigating to a new route we overwrite the top virtuoso state and push an empty state
    // for the new route.
    this.virtuoso?.getState(state => {
      this.virtuosoStateStack.pop();
      this.virtuosoStateStack.push(state);
    });

    // When navigating forwards to a new route, we clear the forward stack.
    runInAction(() => {
      this.forwardStack = [];
    });

    this.store.sidePanel.setSidePanelFocused(false);
    this.pushToStack(targetPath);
    this.navigate(targetPath, config?.routerOptions);
  };

  goUpIfActivePathIs = (path: To) => {
    if (this.activePath === path) {
      this.goUp();
    }
  };

  goUp = () => {
    setTimeout(() => {
      if (!this.navigate) return;
      goUp(this);
    }, 0);
  };

  goBack = () => {
    setTimeout(() => {
      runInAction(() => {
        if (!this.navigate) return;
        goBack(this, this.navigate);
      });
    }, 0);
  };

  goForward = () => {
    setTimeout(() => {
      runInAction(() => {
        if (!this.navigate) return;
        goForward(this, this.navigate);
      });
    }, 0);
  };

  get canGoBack() {
    return this.stack.length > 1;
  }

  get canGoForward() {
    return this.forwardStack.length > 0;
  }

  /**
   * These are all the helper functions used for navigation in our app.
   *
   * We strongly prefer this over calling "this.navigateTo({appRoutes.home({}).path);" directly,
   * because it allows us to have more control over the "navigation stack".
   *
   * E.g. in certain cases,
   *   - we want to modify the input arguments
   *   - we want to augment the path name
   *   - we want to keep track of certain paths being navigated to
   *   - we want to support CMD-click to open in inactive pane
   *   - ...
   *
   * Usage:
   * - Normal click: this.store.navigation.goToCollection({ collectionId: "123" })
   * - CMD-click: this.store.navigation.goToCollection({ collectionId: "123", event: mouseEvent })
   *
   * (Yes it is a lot of boilerplate, but it is worth it to have "one way" to do it.)
   */

  goToLogOut = () => {
    this.navigateTo({ appRoute: appRoutes.logOut({}) });
  };

  goToLogIn = () => {
    this.navigateTo({ appRoute: appRoutes.logIn({}) });
  };

  goToInvites = ({ inviteId, token }: { inviteId: string; token?: string }) => {
    const search = token ? `?token=${encodeURIComponent(token)}` : "";
    this.navigateTo({
      to: {
        pathname: appRoutes.invites({ params: { inviteId } }).path,
        search,
      },
    });
  };

  goToCollections = ({
    event,
    openInSameTab,
  }: {
    event?: NavigationMouseEvent;
    openInSameTab?: boolean;
  } = {}) => {
    const strategy = isCmdClick(event)
      ? TabOpeningStrategy.INACTIVE_PANE
      : openInSameTab
        ? TabOpeningStrategy.SAME_TAB
        : TabOpeningStrategy.NEW_TAB;
    this.tabs.openTab({ path: appRoutes.collections({}), strategy });
  };

  goToChatConversation = ({
    conversationId,
    event,
    openInSameTab,
  }: {
    conversationId: string;
    replace?: boolean;
    event?: NavigationMouseEvent;
    openInSameTab?: boolean;
  }) => {
    this.store.sidePanel.close();
    const strategy = isCmdClick(event)
      ? TabOpeningStrategy.INACTIVE_PANE
      : openInSameTab
        ? TabOpeningStrategy.SAME_TAB
        : TabOpeningStrategy.NEW_TAB;
    this.tabs.openTab({
      path: appRoutes.chatConversation({ params: { sessionId: conversationId } }),
      strategy,
    });
  };

  goToExplore = ({
    event,
    openInSameTab,
  }: {
    event?: NavigationMouseEvent;
    openInSameTab?: boolean;
  } = {}) => {
    const strategy = isCmdClick(event)
      ? TabOpeningStrategy.INACTIVE_PANE
      : openInSameTab
        ? TabOpeningStrategy.SAME_TAB
        : TabOpeningStrategy.NEW_TAB;
    this.tabs.openTab({ path: appRoutes.explore({}), strategy });
  };

  goToExploreTopicDetail = ({
    topicId,
    event,
    openInSameTab,
  }: {
    topicId: string;
    event?: NavigationMouseEvent;
    openInSameTab?: boolean;
  }) => {
    const strategy = isCmdClick(event)
      ? TabOpeningStrategy.INACTIVE_PANE
      : openInSameTab
        ? TabOpeningStrategy.SAME_TAB
        : TabOpeningStrategy.NEW_TAB;
    this.tabs.openTab({
      path: appRoutes.exploreTopicDetail({ params: { topicId } }).path,
      strategy,
    });
  };

  goToIntegrations = ({
    event,
    openInSameTab,
  }: {
    event?: NavigationMouseEvent;
    openInSameTab?: boolean;
  } = {}) => {
    const strategy = isCmdClick(event)
      ? TabOpeningStrategy.INACTIVE_PANE
      : openInSameTab
        ? TabOpeningStrategy.SAME_TAB
        : TabOpeningStrategy.NEW_TAB;
    this.tabs.openTab({ path: appRoutes.integrations({}), strategy });
  };

  goToIntegrationsImports = ({
    event,
    openInSameTab,
  }: {
    event?: NavigationMouseEvent;
    openInSameTab?: boolean;
  } = {}) => {
    const strategy = isCmdClick(event)
      ? TabOpeningStrategy.INACTIVE_PANE
      : openInSameTab
        ? TabOpeningStrategy.SAME_TAB
        : TabOpeningStrategy.NEW_TAB;
    this.tabs.openTab({ path: appRoutes.integrationsImports({}), strategy });
  };

  goToIntegrationsApi = ({
    event,
    openInSameTab,
  }: {
    event?: NavigationMouseEvent;
    openInSameTab?: boolean;
  } = {}) => {
    const strategy = isCmdClick(event)
      ? TabOpeningStrategy.INACTIVE_PANE
      : openInSameTab
        ? TabOpeningStrategy.SAME_TAB
        : TabOpeningStrategy.NEW_TAB;
    this.tabs.openTab({ path: appRoutes.integrationsApi({}), strategy });
  };

  goToIntegrationsZapier = ({
    event,
    openInSameTab,
  }: {
    event?: NavigationMouseEvent;
    openInSameTab?: boolean;
  } = {}) => {
    const strategy = isCmdClick(event)
      ? TabOpeningStrategy.INACTIVE_PANE
      : openInSameTab
        ? TabOpeningStrategy.SAME_TAB
        : TabOpeningStrategy.NEW_TAB;
    this.tabs.openTab({ path: appRoutes.integrationsZapier({}), strategy });
  };

  goToIntegrationsEmail = ({
    event,
    openInSameTab,
  }: {
    event?: NavigationMouseEvent;
    openInSameTab?: boolean;
  } = {}) => {
    const strategy = isCmdClick(event)
      ? TabOpeningStrategy.INACTIVE_PANE
      : openInSameTab
        ? TabOpeningStrategy.SAME_TAB
        : TabOpeningStrategy.NEW_TAB;
    this.tabs.openTab({ path: appRoutes.integrationsEmail({}), strategy });
  };

  goToIntegrationsTextMessage = ({
    event,
    openInSameTab,
  }: {
    event?: NavigationMouseEvent;
    openInSameTab?: boolean;
  } = {}) => {
    const strategy = isCmdClick(event)
      ? TabOpeningStrategy.INACTIVE_PANE
      : openInSameTab
        ? TabOpeningStrategy.SAME_TAB
        : TabOpeningStrategy.NEW_TAB;
    this.tabs.openTab({ path: appRoutes.integrationsTextMessage({}), strategy });
  };

  goToIntegrationsChromeExtension = ({
    event,
    openInSameTab,
  }: {
    event?: NavigationMouseEvent;
    openInSameTab?: boolean;
  } = {}) => {
    const strategy = isCmdClick(event)
      ? TabOpeningStrategy.INACTIVE_PANE
      : openInSameTab
        ? TabOpeningStrategy.SAME_TAB
        : TabOpeningStrategy.NEW_TAB;
    this.tabs.openTab({ path: appRoutes.integrationsChromeExtension({}), strategy });
  };

  goToImportFrom1dot0 = ({
    event,
    openInSameTab,
  }: {
    event?: NavigationMouseEvent;
    openInSameTab?: boolean;
  } = {}) => {
    const strategy = isCmdClick(event)
      ? TabOpeningStrategy.INACTIVE_PANE
      : openInSameTab
        ? TabOpeningStrategy.SAME_TAB
        : TabOpeningStrategy.NEW_TAB;
    this.tabs.openTab({ path: appRoutes.importFrom1dot0({}), strategy });
  };

  goToImportBatchNotes = ({
    importBatchId,
    event,
    openInSameTab,
  }: {
    importBatchId: string;
    event?: NavigationMouseEvent;
    openInSameTab?: boolean;
  }) => {
    const strategy = isCmdClick(event)
      ? TabOpeningStrategy.INACTIVE_PANE
      : openInSameTab
        ? TabOpeningStrategy.SAME_TAB
        : TabOpeningStrategy.NEW_TAB;
    this.tabs.openTab({
      path: appRoutes.importBatchNotes({ params: { importBatchId } }),
      strategy,
    });
  };

  goToImportBatchCollections = ({
    importBatchId,
    event,
    openInSameTab,
  }: {
    importBatchId: string;
    event?: NavigationMouseEvent;
    openInSameTab?: boolean;
  }) => {
    const strategy = isCmdClick(event)
      ? TabOpeningStrategy.INACTIVE_PANE
      : openInSameTab
        ? TabOpeningStrategy.SAME_TAB
        : TabOpeningStrategy.NEW_TAB;
    this.tabs.openTab({
      path: appRoutes.importBatchCollections({ params: { importBatchId } }),
      strategy,
    });
  };

  goToCollection = ({
    collectionId,
    event,
    activeTabId,
    openInSameTab,
    openInInactivePane,
  }: {
    collectionId: string;
    event?: NavigationMouseEvent;
    activeTabId?: string;
    openInSameTab?: boolean;
    openInInactivePane?: boolean;
  }) => {
    const strategy =
      isCmdClick(event) || openInInactivePane
        ? TabOpeningStrategy.INACTIVE_PANE
        : openInSameTab
          ? TabOpeningStrategy.SAME_TAB
          : TabOpeningStrategy.NEW_TAB;
    this.tabs.openTab({
      path: appRoutes.collectionsView({ params: { collectionId } }),
      strategy,
      config: { activeTabId },
    });
  };

  goToNote = ({
    noteId,
    highlightText,
    autoFocus,
    resetStack,
    autoStartVoice,
    event,
    activeTabId,
    openInSameTab,
  }: {
    noteId: string;
    highlightText?: string | null;
    autoFocus?: boolean;
    resetStack?: boolean;
    autoStartVoice?: boolean;
    event?: NavigationMouseEvent;
    activeTabId?: string;
    openInSameTab?: boolean;
  }) => {
    const config = {
      routerOptions: {
        state: { highlightText, autoFocus, autoStartVoice, resetStack },
      },
    };

    const strategy = isCmdClick(event)
      ? TabOpeningStrategy.INACTIVE_PANE
      : openInSameTab
        ? TabOpeningStrategy.SAME_TAB
        : TabOpeningStrategy.NEW_TAB;
    this.tabs.openTab({
      path: appRoutes.notesView({ params: { noteId } }).path,
      strategy,
      config: { ...config, activeTabId },
    });
  };

  goToMention = ({
    id,
    mentionKind,
    url,
    highlightText,
    keys,
    activeTabId,
    openInSameTab,
    openInBackground,
    historyVersion,
    suppressSidePanelRouteHandler,
  }: {
    url: string;
    mentionKind: MemCommonMentionKind | null;
    id: string;
    highlightText?: string;
    keys?: Parameters<INotesViewPageStore["goToMention"]>[0]["keys"];
    activeTabId?: string;
    openInSameTab?: boolean;
    /** If true, opens in background without switching focus */
    openInBackground?: boolean;
    historyVersion?: number;
    suppressSidePanelRouteHandler?: boolean;
  }) => {
    // Other clients and systems MAY store IDs in all caps, so we normalize them to lowercase for the web client router.
    const lowerCaseId = id.toLowerCase();

    switch (mentionKind) {
      case MemCommonMentionKind.Collection: {
        const strategy = (() => {
          if (keys?.metaKey) {
            return openInBackground
              ? TabOpeningStrategy.INACTIVE_PANE_BACKGROUND
              : TabOpeningStrategy.INACTIVE_PANE;
          }
          if (openInSameTab) return TabOpeningStrategy.SAME_TAB;
          return openInBackground
            ? TabOpeningStrategy.NEW_TAB_BACKGROUND
            : TabOpeningStrategy.NEW_TAB;
        })();

        this.tabs.openTab({
          path: appRoutes.collectionsView({ params: { collectionId: lowerCaseId } }),
          strategy,
          config: { activeTabId, suppressSidePanelRouteHandler },
        });
        break;
      }
      case MemCommonMentionKind.Note: {
        const config = {
          routerOptions: { state: { highlightText, historyVersion } },
          suppressSidePanelRouteHandler,
        };
        const strategy = (() => {
          if (keys?.metaKey) {
            return openInBackground
              ? TabOpeningStrategy.INACTIVE_PANE_BACKGROUND
              : TabOpeningStrategy.INACTIVE_PANE;
          }
          if (openInSameTab) return TabOpeningStrategy.SAME_TAB;
          return openInBackground
            ? TabOpeningStrategy.NEW_TAB_BACKGROUND
            : TabOpeningStrategy.NEW_TAB;
        })();

        this.tabs.openTab({
          path: appRoutes.notesView({ params: { noteId: lowerCaseId } }),
          strategy,
          config: { ...config, activeTabId },
        });
        break;
      }
      default: {
        windowModule.openInNewTab({ url: new URL(url) });
      }
    }
  };

  goToTrash = ({
    event,
    openInSameTab,
  }: {
    event?: NavigationMouseEvent;
    openInSameTab?: boolean;
  } = {}) => {
    const strategy = isCmdClick(event)
      ? TabOpeningStrategy.INACTIVE_PANE
      : openInSameTab
        ? TabOpeningStrategy.SAME_TAB
        : TabOpeningStrategy.NEW_TAB;
    this.tabs.openTab({ path: appRoutes.trash({}), strategy });
  };

  goToAdmin = ({
    event,
    openInSameTab,
  }: {
    event?: NavigationMouseEvent;
    openInSameTab?: boolean;
  } = {}) => {
    const strategy = isCmdClick(event)
      ? TabOpeningStrategy.INACTIVE_PANE
      : openInSameTab
        ? TabOpeningStrategy.SAME_TAB
        : TabOpeningStrategy.NEW_TAB;
    this.tabs.openTab({ path: appRoutes.admin({}), strategy });
  };

  goToTemplates = ({
    event,
    openInSameTab,
  }: {
    event?: NavigationMouseEvent;
    openInSameTab?: boolean;
  } = {}) => {
    const strategy = isCmdClick(event)
      ? TabOpeningStrategy.INACTIVE_PANE
      : openInSameTab
        ? TabOpeningStrategy.SAME_TAB
        : TabOpeningStrategy.NEW_TAB;
    this.tabs.openTab({ path: appRoutes.templatesList({}), strategy });
  };

  goToTemplate = ({
    templateId,
    event,
    openInSameTab,
  }: {
    templateId: string;
    event?: NavigationMouseEvent;
    openInSameTab?: boolean;
  }) => {
    const strategy = isCmdClick(event)
      ? TabOpeningStrategy.INACTIVE_PANE
      : openInSameTab
        ? TabOpeningStrategy.SAME_TAB
        : TabOpeningStrategy.NEW_TAB;
    this.tabs.openTab({ path: appRoutes.templatesView({ params: { templateId } }), strategy });
  };

  goToSessions = ({
    event,
    openInSameTab,
  }: {
    event?: NavigationMouseEvent;
    openInSameTab?: boolean;
  } = {}) => {
    const strategy = isCmdClick(event)
      ? TabOpeningStrategy.INACTIVE_PANE
      : openInSameTab
        ? TabOpeningStrategy.SAME_TAB
        : TabOpeningStrategy.NEW_TAB;
    this.tabs.openTab({ path: appRoutes.sessionsList({}), strategy });
  };

  goToSession = ({
    sessionId,
    event,
    openInSameTab,
  }: {
    sessionId: string;
    event?: NavigationMouseEvent;
    openInSameTab?: boolean;
  }) => {
    const strategy = isCmdClick(event)
      ? TabOpeningStrategy.INACTIVE_PANE
      : openInSameTab
        ? TabOpeningStrategy.SAME_TAB
        : TabOpeningStrategy.NEW_TAB;
    this.tabs.openTab({ path: appRoutes.sessionsView({ params: { sessionId } }), strategy });
  };

  goToAudioRecordings = ({
    event,
    openInSameTab,
  }: {
    event?: NavigationMouseEvent;
    openInSameTab?: boolean;
  } = {}) => {
    const strategy = isCmdClick(event)
      ? TabOpeningStrategy.INACTIVE_PANE
      : openInSameTab
        ? TabOpeningStrategy.SAME_TAB
        : TabOpeningStrategy.NEW_TAB;
    this.tabs.openTab({ path: appRoutes.audioRecordings({}), strategy });
  };

  goToAudioRecording = ({
    recordingId,
    event,
    openInSameTab,
  }: {
    recordingId: string;
    event?: NavigationMouseEvent;
    openInSameTab?: boolean;
  }) => {
    const strategy = isCmdClick(event)
      ? TabOpeningStrategy.INACTIVE_PANE
      : openInSameTab
        ? TabOpeningStrategy.SAME_TAB
        : TabOpeningStrategy.NEW_TAB;
    this.tabs.openTab({ path: appRoutes.audioRecording({ params: { recordingId } }), strategy });
  };

  goToAudioRecordingCreate = ({
    event,
    openInSameTab,
  }: {
    event?: NavigationMouseEvent;
    openInSameTab?: boolean;
  } = {}) => {
    const strategy = isCmdClick(event)
      ? TabOpeningStrategy.INACTIVE_PANE
      : openInSameTab
        ? TabOpeningStrategy.SAME_TAB
        : TabOpeningStrategy.NEW_TAB;
    this.tabs.openTab({ path: appRoutes.audioRecordingCreate({}), strategy });
  };

  goToSettings = ({
    event,
    openInSameTab,
  }: {
    event?: NavigationMouseEvent;
    openInSameTab?: boolean;
  } = {}) => {
    const strategy = isCmdClick(event)
      ? TabOpeningStrategy.INACTIVE_PANE
      : openInSameTab
        ? TabOpeningStrategy.SAME_TAB
        : TabOpeningStrategy.NEW_TAB;
    this.tabs.openTab({ path: appRoutes.settings({}), strategy });
  };

  goToSettingsEmailPreferences = (event?: NavigationMouseEvent) => {
    const strategy = isCmdClick(event)
      ? TabOpeningStrategy.INACTIVE_PANE
      : TabOpeningStrategy.SAME_TAB;
    this.tabs.openTab({ path: appRoutes.settingsEmailPreferences({}), strategy });
  };

  goToSettingsSubscriptionManagement = (event?: NavigationMouseEvent) => {
    const strategy = isCmdClick(event)
      ? TabOpeningStrategy.INACTIVE_PANE
      : TabOpeningStrategy.SAME_TAB;
    this.tabs.openTab({
      path: appRoutes.settingsSubscriptionManagement({}),
      strategy,
    });
  };

  goToSettingsAccountManagement = (event?: NavigationMouseEvent) => {
    const strategy = isCmdClick(event)
      ? TabOpeningStrategy.INACTIVE_PANE
      : TabOpeningStrategy.SAME_TAB;
    this.tabs.openTab({ path: appRoutes.settingsAccountManagement({}), strategy });
  };

  goToSettingsDebugAccount = (event?: NavigationMouseEvent) => {
    const strategy = isCmdClick(event)
      ? TabOpeningStrategy.INACTIVE_PANE
      : TabOpeningStrategy.SAME_TAB;
    this.tabs.openTab({ path: appRoutes.settingsDebugAccount({}), strategy });
  };

  goToSettingsDebugSandbox = (event?: NavigationMouseEvent) => {
    const strategy = isCmdClick(event)
      ? TabOpeningStrategy.INACTIVE_PANE
      : TabOpeningStrategy.SAME_TAB;
    this.tabs.openTab({ path: appRoutes.settingsDebugSandbox({}), strategy });
  };

  goToSettingsExports = (event?: NavigationMouseEvent) => {
    const strategy = isCmdClick(event)
      ? TabOpeningStrategy.INACTIVE_PANE
      : TabOpeningStrategy.SAME_TAB;
    this.tabs.openTab({ path: appRoutes.settingsExports({}), strategy });
  };

  goToSettingsAccountMigration = (event?: NavigationMouseEvent) => {
    const strategy = isCmdClick(event)
      ? TabOpeningStrategy.INACTIVE_PANE
      : TabOpeningStrategy.SAME_TAB;
    this.tabs.openTab({ path: appRoutes.settingsAccountMigration({}), strategy });
  };

  goToSearch = ({
    searchParams,
    config,
    event,
    openInSameTab,
    retriggerSearch,
    openInBackground,
  }: {
    searchParams: Partial<SearchEngineParams>;
    config?: { replace?: boolean };
    event?: NavigationMouseEvent;
    openInSameTab?: boolean;
    openInBackground?: boolean;
    retriggerSearch?: boolean;
  }) => {
    const searchQueryStr = urlParamsModule.search.stringify({ searchParams });
    const path = {
      pathname: appRoutes.search({}).path,
      search: searchQueryStr,
    };
    const navigationConfig = {
      routerOptions: {
        replace: config?.replace,
        state: { retriggerSearch },
      },
    };
    const strategy = (() => {
      if (isCmdClick(event)) {
        return openInBackground
          ? TabOpeningStrategy.INACTIVE_PANE_BACKGROUND
          : TabOpeningStrategy.INACTIVE_PANE;
      }
      if (openInSameTab) return TabOpeningStrategy.SAME_TAB;
      if (openInBackground) return TabOpeningStrategy.NEW_TAB_BACKGROUND;
      return TabOpeningStrategy.NEW_TAB;
    })();
    this.tabs.openTab({
      path,
      strategy,
      config: navigationConfig,
    });
  };

  goToNotes = (
    {
      homeParams,
      config,
      event,
      openInSameTab,
    }: {
      homeParams?: Partial<HomeParams>;
      config?: { replace?: boolean };
      event?: NavigationMouseEvent;
      openInSameTab?: boolean;
    } = { homeParams: {} }
  ) => {
    const searchQueryStr = urlParamsModule.home.stringify({ homeParams: homeParams || {} });
    const path = {
      pathname: appRoutes.notesList({}).path,
      search: searchQueryStr,
    };
    const navigationConfig = { routerOptions: { replace: config?.replace } };

    const strategy = isCmdClick(event)
      ? TabOpeningStrategy.INACTIVE_PANE
      : openInSameTab
        ? TabOpeningStrategy.SAME_TAB
        : TabOpeningStrategy.NEW_TAB;
    this.tabs.openTab({
      path,
      strategy,
      config: navigationConfig,
    });
  };

  goToSavedSearch = ({
    savedSearchId,
    event,
    openInSameTab,
  }: {
    savedSearchId: string;
    event?: NavigationMouseEvent;
    openInSameTab?: boolean;
  }) => {
    const savedSearchObservable = this.store.savedSearches.getSavedSearchObservableById({
      savedSearchId,
    });

    if (savedSearchObservable) {
      this.goToSearch({
        searchParams: { ...savedSearchObservable.savedSearchParams },
        event,
        openInSameTab,
      });
    }
  };

  openExternalHelpAndSupport = () => {
    windowModule.openInNewTab({ url: new URL(APP_CONFIG.URLS.HELP_AND_SUPPORT) });
  };

  goToMemIt = ({ sessionId }: { sessionId: string }) => {
    this.store.sidePanel.goToMemIt(sessionId);
  };
}
